import { Component, Event, h, Host, Prop } from "@stencil/core";
import type { EventEmitter } from "@stencil/core";
import { BOT_IS_LOADING } from "../../../global/variables";

import {
    ButtonSuggestion,
    ConversationLink,
    ConversationMessage,
    DefaultText,
    LinkSuggestion,
    TwoColumnImages,
    UserMessage,
} from "../../../utils/backend/reply-parsed";

import type {
    AnswerBy,
    Conversation,
} from "../../../utils/backend/reply-parsed";

@Component({
    tag: "leichte-sprache-conversation",
    styleUrl: "leichte-sprache-conversation.scss",
})
export class LeichteSpracheConversation {
    @Prop() conversation: Conversation = [];
    @Prop() answerBy: AnswerBy;
    @Prop() loading: boolean;
    @Event() choiceSelected: EventEmitter;
    @Event() linkSuggestionSelected: EventEmitter<LinkSuggestion>;
    @Event() buttonSuggestionSelected: EventEmitter<ButtonSuggestion>;
    latestUserMessage: HTMLElement;

    componentDidRender() {
        if (this.latestUserMessage?.scrollIntoView) {
            this.latestUserMessage.scrollIntoView({
                block: "start",
                behavior: "smooth",
            });
        }
        this.restoreFirefoxFocus();
    }

    restoreFirefoxFocus() {
        const isFirefox =
            navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
        if (!isFirefox || !this.latestUserMessage) {
            return;
        }
        // Without this solution, Firefox does not remember the last focused element after clicking a multiple choice button.
        // The next time the user hits tab, the first element of the page will be focused instead of the next element inside the bot.
        // To fix this, we temporarily make the default message bubble focusable and secretly focus it without showing a focus highlight.
        // Additionally we need to set :focus {outline: none;}
        this.latestUserMessage.setAttribute("tabindex", "-1");
        this.latestUserMessage.focus();
        this.latestUserMessage.removeAttribute("tabindex");
        // However the solution above makes VoiceOver on Mac loose focus when removing tabindex and starts from the top of conversation.
        // Maybe  screen reader users could just use tab instead, but its not optimal
    }

    defaultText = (defaultText: DefaultText) => {
        return (
            <div class="message-row">
                <leichte-sprache-bot-avatar aria-hidden="true"></leichte-sprache-bot-avatar>
                <leichte-sprache-bubble
                    text={
                        '<span class="sr-only">Bot sagt: </span>' +
                        defaultText.text
                    }
                    variant="bot"
                ></leichte-sprache-bubble>
            </div>
        );
    };
    conversationLink = (link: ConversationLink) => {
        // Future Task Maybe I need sr-only here as well instead of aria-label in case Chrome + JAWS reads the link as first element (out of order)
        return (
            <a
                class="multiple-choice-link"
                innerHTML={link.title}
                href={link.url}
                aria-label={link.screenReader ? link.screenReader : link.title}
                target="_blank"
                rel="noopener noreferrer"
            ></a>
        );
    };
    twoColumnImages = (message: TwoColumnImages) => {
        return (
            <div class="message-row">
                <leichte-sprache-bot-avatar aria-hidden="true"></leichte-sprache-bot-avatar>
                <leichte-sprache-bubble-two-column-images
                    text={
                        '<span class="sr-only">Bot sagt: </span>' + message.text
                    }
                    url={message.img}
                ></leichte-sprache-bubble-two-column-images>
            </div>
        );
    };

    userMessage = (userMessage: UserMessage) => {
        const latestUserMessage = [...this.conversation]
            .reverse()
            .find(
                (message: ConversationMessage) => message.type === "userMessage"
            );

        return (
            <div
                class="message-row message-row--user"
                ref={(el) => {
                    if (latestUserMessage === userMessage) {
                        this.latestUserMessage = el as HTMLDivElement;
                    }
                }}
            >
                <leichte-sprache-bubble
                    variant="user"
                    text={
                        '<span class="sr-only">Nutzer sagt: </span>' +
                        userMessage.text
                    }
                ></leichte-sprache-bubble>
                <leichte-sprache-user-avatar aria-hidden="true"></leichte-sprache-user-avatar>
            </div>
        );
    };

    // @ts-ignore
    getButtons = () => {
        if (!this.answerBy) {
            return null;
        }
        switch (this.answerBy.type) {
            case "cookies":
            case "textInput":
            case "salaryInput":
                return null;
            case "multipleChoice":
                return this.answerBy.choices.map((choice) => {
                    return (
                        <button
                            key={choice.title}
                            class="multiple-choice-button"
                            innerHTML={
                                '<span class="sr-only">Klicken sie für: </span>' +
                                choice.title
                            }
                            onClick={() => this.choiceSelected.emit(choice)}
                        ></button>
                    );
                });
            case "suggestions":
                return this.answerBy.elements.map((suggestion) => {
                    switch (suggestion.type) {
                        case "link":
                            return (
                                <a
                                    class="multiple-choice-link"
                                    href={suggestion.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() =>
                                        this.linkSuggestionSelected.emit(
                                            suggestion
                                        )
                                    }
                                >
                                    <span class="sr-only">
                                        Klicken sie für: {suggestion.screenReader || suggestion.title}
                                    </span>
                                    <span aria-hidden="true">
                                        {suggestion.title}
                                    </span>
                                </a>
                            );
                        case "button":
                            return (
                                <button
                                    class="multiple-choice-button"
                                    innerHTML={`<span class="sr-only">Klicken sie für: ${
                                        suggestion.screenReader ||
                                        suggestion.title
                                    }</span><span aria-hidden="true">${
                                        suggestion.title
                                    }</span>`}
                                    onClick={() =>
                                        this.buttonSuggestionSelected.emit(
                                            suggestion
                                        )
                                    }
                                ></button>
                            );
                    }
                });
        }
    };

    render() {
        return (
            <Host
                id="ls-conversation"
                tabindex="0"
                role="log"
                aria-label="Nachrichten Verlauf"
                aria-live="polite"
            >
                {/* Messages */}
                {this.conversation.map((message) => {
                    switch (message.type) {
                        case "defaultText":
                            return this.defaultText(message);
                        case "twoColumnImages":
                            return this.twoColumnImages(message);
                        case "userMessage":
                            return this.userMessage(message);
                        case "conversationLink":
                            return this.conversationLink(message);
                    }
                })}
                {this.getButtons()}
                {this.loading && (
                    <div
                        id="ls-bot-loading"
                        class="message-row"
                        aria-label={BOT_IS_LOADING}
                    >
                        <leichte-sprache-bot-avatar></leichte-sprache-bot-avatar>
                        <leichte-sprache-bubble variant="loading"></leichte-sprache-bubble>
                    </div>
                )}
            </Host>
        );
    }
}
